/**
 * Durations and their families (BUILDLIST 64.4; decision 110). Build a duration with `turns`, `seconds` or `days`;
 * convert it once, at the rule, with `inSeconds` / `inTurns`. Turns and seconds convert by `SECONDS_PER_TURN`; days
 * are the sector's clock and don't convert to the battle clocks (a battle's span in days is the sector's business).
 * Pure and deterministic.
 */
import type { Duration, DurationFamily } from '../types';
import { SECONDS_PER_TURN } from '../constants';

export const turns = (value: number): Duration<'turns'> => ({ family: 'turns', value });
export const seconds = (value: number): Duration<'seconds'> => ({ family: 'seconds', value });
export const days = (value: number): Duration<'days'> => ({ family: 'days', value });

function noDays(d: Duration, to: DurationFamily): never {
  throw new Error(`a duration in days does not convert to ${to}: days are the sector's clock`);
}

/** The duration in seconds: turns × SECONDS_PER_TURN; seconds as they are. */
export function inSeconds(d: Duration): number {
  if (d.family === 'seconds') return d.value;
  if (d.family === 'turns') return d.value * SECONDS_PER_TURN;
  return noDays(d, 'seconds');
}

/** The duration in turns: seconds ÷ SECONDS_PER_TURN; turns as they are. */
export function inTurns(d: Duration): number {
  if (d.family === 'turns') return d.value;
  if (d.family === 'seconds') return d.value / SECONDS_PER_TURN;
  return noDays(d, 'turns');
}

/** A turn-counted duration's seconds form: the one conversion, at the rule. */
export function secondsTwinOf(d: Duration<'turns'>): Duration<'seconds'> {
  return seconds(inSeconds(d));
}
